import {
  InputOtp,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
} from '@heroui/react';
import { FaInfoCircle, FaTrash } from 'react-icons/fa';
import CustomInput from '../../../components/CustomInput';
import CustomButton from '../../../components/CustomButton';
import { useState } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import { useCountdown } from '../../../../hook/commonHook';
import { handleLogout } from '../../../components/auth/apiAuth';
import { validateVerificationCode } from '../../../../service/twoFactorAuthService';
import { isMatch } from '../../../../utils/helper';
import {
  useRequestAccountDeleteOtp,
  useResendAccountDeactivationAndDeleteOtp,
  useVerifyAccountDeleteOtp,
} from '../../../../hook/useMutationData';
import { ACCOUNT_STATUS } from '../../../../utils/enum';
import CustomAlert from '../../../components/CustomAlert';

const RESEND_DELAY = 60;

const AccountDelete = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { user, customerAccountStatus } = useAuth();

  const [confirmEmail, setConfirmEmail] = useState('');
  const [deleteVerificationCode, setDeleteVerificationCode] = useState('');
  const [deleteStep, setDeleteStep] = useState<'request' | 'verify'>('request');
  const [securityToken, setSecurityToken] = useState<string | null>('');
  const [alertMessage, setAlertMessage] = useState<{
    msg: string;
    type: 'error' | 'success';
  } | null>(null);

  const { countdown, startCountdown, resetCountdown, isActive } =
    useCountdown();

  const { mutateAsync: requestOtp, isPending: isRequestOtpPending } =
    useRequestAccountDeleteOtp();
  const { mutateAsync: verifyOtp, isPending: isOtpVerifyLoading } =
    useVerifyAccountDeleteOtp();
  const {
    mutateAsync: resendOtp,
    isPending: isResendOtpLoading,
    error,
  } = useResendAccountDeactivationAndDeleteOtp();

  const currentUserId = user?.userId?.trim();
  const currentEmail = user?.email?.trim();

  console.log('Error: ', error);

  // STEP - 1 - REQUEST OTP
  const handleDeleteRequest = async () => {
    resetCountdown();

    try {
      if (!isMatch(user?.email, confirmEmail)) {
        setAlertMessage({
          msg: 'The email address does not match.',
          type: 'error',
        });
        return;
      }

      if (!currentUserId || !currentEmail) {
        setAlertMessage({
          msg: 'Unable to identify user details. Please re-login.',
          type: 'error',
        });
        return;
      }

      const result = await requestOtp('customer');

      console.table(result);

      setSecurityToken(result.securityToken || '');

      setAlertMessage({
        msg: result.message || 'OTP code sent. Please check your email.',
        type: 'success',
      });

      setDeleteStep('verify');
      startCountdown(RESEND_DELAY);
    } catch (error: any) {
      console.error('Error requesting account delete:', error);

      let errorMessage =
        error.message || 'Failed to request account delete. Please try again.';

      setAlertMessage({
        msg:
          errorMessage || 'Failed to request account delete. Please try again.',
        type: 'error',
      });
    }
  };

  //SUBMIT OTP
  const handleConfirmDelete = async () => {
    if (!validateVerificationCode(deleteVerificationCode)) {
      setAlertMessage({
        msg: 'Please enter a valid 6-digit verification code.',
        type: 'error',
      });

      return;
    }

    if (!currentUserId || !currentEmail || !securityToken) {
      setAlertMessage({
        msg: 'Missing verification context. Please request a new code and try again.',
        type: 'error',
      });
      return;
    }

    try {
      console.table({
        accountType: 'customer',
        otp: deleteVerificationCode,
        securityToken,
      });

      const result = await verifyOtp({
        accountType: 'customer',
        otp: deleteVerificationCode,
        securityToken,
      });

      setAlertMessage({
        msg: result?.message || 'Account Delete successful.',
        type: 'success',
      });

      onClose();
      handleLogout();
    } catch (error: any) {
      console.error('Error confirming account delete:', error);

      let errorMessage = 'Failed to confirm account delete. Please try again.';

      if (error instanceof Error) {
        errorMessage = error.message;
      }

      setAlertMessage({
        msg:
          errorMessage || 'Failed to confirm account delete. Please try again.',
        type: 'error',
      });
    }
  };

  //RESEND CODE
  const handleResendVerificationCode = async () => {
    try {
      resetCountdown();

      const result = await resendOtp({
        accountType: 'customer',
        action: 'deactivate',
      });

      console.table(result);
      setAlertMessage({
        msg:
          result.message ||
          'Code resent successfully. Please check your email.',
        type: 'success',
      });
      setSecurityToken(result.securityToken || null);
      startCountdown(RESEND_DELAY);
    } catch (error: any) {
      console.error('Error resending verification code:', error);

      let errorMessage =
        error.message || 'Something went wrong. Please try again later';

      setAlertMessage({ msg: errorMessage, type: 'error' });
    }
  };

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:justify-between items-start gap-3 sm:mt-5">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <FaTrash className="text-red-600" size={14} />
          </div>
          <div className="flex flex-col items-start justify-start">
            <h3 className="text-sm font-semibold text-gray-800">
              Permanent Delete Account
            </h3>
            <p className="text-caption">
              Permanently delete your account and all associated data. This
              action is irreversible.
            </p>
          </div>
        </div>
        <div className="flex justify-end w-full sm:w-auto">
          <CustomButton
            color="danger"
            label="Delete"
            variant="flat"
            className="mt-1"
            onPress={() => {
              setConfirmEmail('');
              setAlertMessage(null);
              onOpen();
            }}
          />
        </div>
      </div>

      <Modal
        isOpen={isOpen}
        onClose={() => {
          setSecurityToken('');
          setConfirmEmail('');
          setDeleteVerificationCode('');
          setDeleteStep('request');
          resetCountdown();
          onClose();
        }}
        placement="center"
        backdrop="blur"
        size="lg"
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            Account Delete
          </ModalHeader>
          <ModalBody>
            {alertMessage != null && (
              <CustomAlert
                description={alertMessage.msg}
                color={alertMessage.type === 'success' ? 'success' : 'danger'}
              />
            )}

            {customerAccountStatus !== ACCOUNT_STATUS.DEACTIVATED && (
              <CustomAlert
                description="Account deletion is unavailable. Please deactivate the account prior to permanent deletion."
                color="danger"
              />
            )}

            {deleteStep === 'request' && (
              <div>
                <div className="bg-orange-50 p-4 rounded border border-orange-200 text-orange-800 flex items-start gap-3 text-sm mb-2">
                  <FaInfoCircle className="mt-0.5 text-orange-600" />
                  <div>
                    <h4 className="font-semibold mb-2">
                      Warning: This action is permanent and irreversible
                    </h4>
                    <ul className="list-none space-y-3 text-sm text-gray-700">
                      <li className="flex text-xs items-start gap-2.5">
                        <span className="text-red-500 font-bold mt-0.5">✕</span>
                        <span>
                          Your account and profile will be{' '}
                          <strong>deleted immediately</strong>. You will be
                          logged out instantly.
                        </span>
                      </li>

                      <li className="flex text-xs items-start gap-2.5">
                        <span className="text-red-500 font-bold mt-0.5">✕</span>
                        <span>
                          All your personal data, preferences, and saved
                          settings will be <strong>permanently purged</strong>.
                          You cannot recover this information later.
                        </span>
                      </li>

                      <li className="flex text-xs items-start gap-2.5">
                        <span className="text-red-500 font-bold mt-0.5">✕</span>
                        <span>
                          <strong>Account Recreation:</strong> User cannot
                          create another provider account using current email
                          after account deletion.
                        </span>
                      </li>

                      <li className="flex text-xs items-start gap-2.5">
                        <span className="text-red-500 font-bold mt-0.5">✕</span>
                        <span>
                          <strong>Legal Retention:</strong> In compliance with
                          financial regulations, past transaction receipts and
                          billing history logs will be securely archived for
                          accounting audits.
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>

                {customerAccountStatus === ACCOUNT_STATUS.DEACTIVATED && (
                  <div className="bg-green-50/60 p-4 rounded-xl border border-green-200 text-green-900 flex items-start gap-3 text-sm">
                    {/* A crisp, clean star icon */}
                    <span
                      className="text-green-600 text-base font-bold mt-0.5 flex-shrink-0"
                      aria-hidden="true"
                    >
                      ★
                    </span>

                    <div className="space-y-1">
                      <p className="font-bold text-green-800 text-sm">
                        Reactivation is easy
                      </p>
                      <p className="text-gray-600 text-xs leading-relaxed">
                        Changed your mind? You can simply reactivate your
                        account and restore all your data at any time.{' '}
                        <button
                          onClick={() => onClose()}
                          className="font-bold underline cursor-pointer text-green-700 hover:text-green-800 transition-colors"
                        >
                          Go to reactivate account
                        </button>
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {deleteStep === 'request' ? (
              <div className="mt-3">
                <CustomInput
                  isRequired
                  label="Type your account email to confirm"
                  type="email"
                  placeholder={user?.email || 'your@email.com'}
                  value={confirmEmail}
                  onValueChange={setConfirmEmail}
                  isDisabled={
                    isRequestOtpPending ||
                    customerAccountStatus !== ACCOUNT_STATUS.DEACTIVATED
                  }
                />
              </div>
            ) : (
              <>
                <div className="mt-1 space-y-3">
                  <div className="flex justify-center items-center">
                    <InputOtp
                      length={6}
                      isRequired
                      placeholder="Enter 6-digit code"
                      value={deleteVerificationCode}
                      onValueChange={setDeleteVerificationCode}
                      isDisabled={isOtpVerifyLoading}
                      variant="bordered"
                      errorMessage="Invalid OTP code"
                    />
                  </div>

                  <div>
                    <p className="text-body mb-3">
                      Didn't receive a code?{' '}
                      <strong
                        className={`hover:underline cursor-pointer text-primary transition-opacity ${
                          isActive || isResendOtpLoading || isOtpVerifyLoading
                            ? 'opacity-50 cursor-not-allowed'
                            : ''
                        }`}
                      >
                        {isResendOtpLoading ? (
                          <span className="inline-flex items-baseline gap-2 ">
                            Sending...
                          </span>
                        ) : (
                          <p
                            onClick={
                              !isActive
                                ? handleResendVerificationCode
                                : undefined
                            }
                            className={`inline-flex items-baseline gap-2  transition-opacity ${
                              !isActive
                                ? 'hover:underline cursor-pointer text-primary'
                                : 'cursor-not-allowed text-gray-500'
                            }`}
                          >
                            {isActive
                              ? `Resend in ${countdown}s`
                              : 'Resend Code'}
                          </p>
                        )}
                      </strong>
                    </p>
                  </div>
                </div>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <CustomButton
              color="default"
              variant="flat"
              label="Cancel"
              onPress={() => {
                setConfirmEmail('');
                setDeleteVerificationCode('');
                setDeleteStep('request');
                onClose();
              }}
              isDisabled={isRequestOtpPending || isOtpVerifyLoading}
            />

            {deleteStep === 'request' && (
              <CustomButton
                color="danger"
                variant="solid"
                label="Request code"
                onPress={handleDeleteRequest}
                isLoading={isRequestOtpPending}
                isDisabled={
                  customerAccountStatus !== ACCOUNT_STATUS.DEACTIVATED
                }
              />
            )}

            {deleteStep === 'verify' && (
              <CustomButton
                color="danger"
                variant="solid"
                label="Confirm Delete"
                onPress={handleConfirmDelete}
                isDisabled={isOtpVerifyLoading}
                isLoading={isOtpVerifyLoading}
              />
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AccountDelete;
